// src/context/AuthContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me"); // cookie sent automatically
      setUser(data?.user || null);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe(); // boot
  }, [fetchMe]);

  /* ============= GOOGLE ============= */
  const loginWithGoogleIdToken = useCallback(async (idToken) => {
    await api.post("/auth/google", { idToken }); // sets cookie
    await fetchMe();
  }, [fetchMe]);

  /* ============= EVM ============= */
  const loginWithEvm = useCallback(async () => {
    if (!window.ethereum) throw new Error("No EVM wallet found");
    const [address] = await window.ethereum.request({ method: "eth_requestAccounts" });
    const addr = String(address).toLowerCase();

    // 1) get server nonce + message
    const { data: nonceRes } = await api.get("/auth/nonce", { params: { address: addr, chain: "evm" } });
    const message = nonceRes?.message;
    if (!message) throw new Error("Failed to get nonce");

    // 2) sign message
    const signature = await window.ethereum.request({
      method: "personal_sign",
      params: [message, addr],
    });

    // 3) verify
    await api.post("/auth/evm/verify", { address: addr, signature, message });
    await fetchMe();
  }, [fetchMe]);

  const linkWalletEvm = useCallback(async () => {
    if (!user) throw new Error("Login first");
    if (!window.ethereum) throw new Error("No EVM wallet found");
    const [address] = await window.ethereum.request({ method: "eth_requestAccounts" });
    const addr = String(address).toLowerCase();

    const { data: nonceRes } = await api.get("/auth/nonce", { params: { address: addr, chain: "evm" } });
    const message = nonceRes?.message;
    if (!message) throw new Error("Failed to get nonce");

    const signature = await window.ethereum.request({
      method: "personal_sign",
      params: [message, addr],
    });

    await api.post("/auth/link-wallet", { chain: "evm", address: addr, signature, message });
    await fetchMe();
  }, [user, fetchMe]);

  /* ============= SOL ============= */
  const loginWithSol = useCallback(async () => {
    const provider = window.solana || window.phantom?.solana;
    if (!provider) throw new Error("No Solana wallet found");

    // connect
    await provider.connect?.();
    const address = provider.publicKey?.toString();
    if (!address) throw new Error("No Solana address");

    // nonce
    const { data: nonceRes } = await api.get("/auth/nonce", { params: { address, chain: "sol" } });
    const message = nonceRes?.message;
    if (!message) throw new Error("Failed to get nonce");

    // sign (Phantom signMessage)
    const encoded = new TextEncoder().encode(message);
    const { signature } = await provider.signMessage(encoded, "utf8");
    const sigB58 = (window.bs58 || (await import("bs58"))).default.encode(signature);

    // verify
    await api.post("/auth/sol/verify", { address, signature: sigB58, message });
    await fetchMe();
  }, [fetchMe]);

  const linkWalletSol = useCallback(async () => {
    if (!user) throw new Error("Login first");
    const provider = window.solana || window.phantom?.solana;
    if (!provider) throw new Error("No Solana wallet found");

    await provider.connect?.();
    const address = provider.publicKey?.toString();
    if (!address) throw new Error("No Solana address");

    const { data: nonceRes } = await api.get("/auth/nonce", { params: { address, chain: "sol" } });
    const message = nonceRes?.message;
    if (!message) throw new Error("Failed to get nonce");

    const encoded = new TextEncoder().encode(message);
    const { signature } = await provider.signMessage(encoded, "utf8");
    const sigB58 = (window.bs58 || (await import("bs58"))).default.encode(signature);

    await api.post("/auth/link-wallet", { chain: "sol", address, signature: sigB58, message });
    await fetchMe();
  }, [user, fetchMe]);

  /* ============= EMAIL/PASSWORD ============= */
  const registerWithEmailPassword = useCallback(async (email, password) => {
    await api.post("/auth/email/register", { email, password }); // returns token too, but cookie is used elsewhere
    await fetchMe();
  }, [fetchMe]);

  const loginWithEmailPassword = useCallback(async (email, password) => {
    await api.post("/auth/email/login", { email, password });
    await fetchMe();
  }, [fetchMe]);

  /* ============= LOGOUT ============= */
  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    authLoading,
    loginWithGoogleIdToken,
    loginWithEvm,
    loginWithSol,
    linkWalletEvm,
    linkWalletSol,
    registerWithEmailPassword,
    loginWithEmailPassword,
    logout,
    refreshMe: fetchMe,
  }), [
    user, authLoading,
    loginWithGoogleIdToken, loginWithEvm, loginWithSol,
    linkWalletEvm, linkWalletSol,
    registerWithEmailPassword, loginWithEmailPassword,
    logout, fetchMe
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
